import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Brain, Target, Sparkles, FileSearch, Users, MessageSquare } from "lucide-react";
import api from "../lib/api";

const STEPS = [
  { n: "01", t: "Upload your career data", d: "Resume, LinkedIn, portfolio, certifications. Our parser does the rest." },
  { n: "02", t: "AI builds your vector profile", d: "Skills, seniority, industry & experience extracted into a structured intelligence profile." },
  { n: "03", t: "Weighted matching engine", d: "Skill 35% · Experience 25% · Industry 15% · Location 10% · Salary 5% · Education 5% · Culture 5%." },
  { n: "04", t: "Get your Match Score %", d: "Recommendations for candidates, AI shortlists for employers — with reasons." },
];

const FEATURES = [
  { icon: FileSearch, t: "Resume AI Parser", d: "NLP + LLM pipeline converts raw work history into structured skills, seniority and industry vectors." },
  { icon: Target, t: "Matching Engine", d: "Transparent weighted scoring across 7 dimensions produces an explainable Match Score %." },
  { icon: Users, t: "Employer Shortlist", d: "AI-ranked candidate shortlists with strengths — surfaced the moment you post a role." },
  { icon: MessageSquare, t: "Recruiter Copilot", d: "“Find candidates with pharma warehouse experience near Toronto.” Natural language search." },
  { icon: Sparkles, t: "Interview Assistant", d: "Likely questions, STAR-method answers and scorecards generated per role." },
  { icon: Brain, t: "Career Chatbot", d: "Job seekers chat with AI to discover their highest-matching roles instantly." },
];

export default function Landing() {
  const [jobs, setJobs] = useState([]);
  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <main data-testid="landing-page">
      {/* HERO */}
      <section className="border-b border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-12 gap-0">
          <div className="lg:col-span-7 py-20 lg:py-28 lg:pr-12 fade-up">
            <div className="inline-flex items-center gap-2 border border-[#0A0A0A] px-3 py-1 mb-8 font-mono2 text-xs uppercase tracking-wider">
              <img src="/darriker-logo.png" alt="" className="w-4 h-4 object-contain" /> AI Talent Matching Engine
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              Hire and get hired by{" "}
              <span className="text-[#002FA7]">match score</span>, not keywords.
            </h1>
            <p className="mt-7 text-lg text-[#4B5563] max-w-xl">
              Darriker AI Match™ reads resumes and job descriptions like a recruiter,
              then ranks every candidate–role pair on a transparent 0–100% scale.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/auth?mode=register&role=candidate"
                data-testid="hero-candidate-cta"
                className="btn-ink px-6 py-3 font-medium rounded-none inline-flex items-center gap-2"
              >
                I'm a candidate <ArrowRight size={18} />
              </Link>
              <Link
                to="/auth?mode=register&role=employer"
                data-testid="hero-employer-cta"
                className="btn-outline px-6 py-3 font-medium rounded-none inline-flex items-center gap-2"
              >
                I'm hiring <ArrowRight size={18} />
              </Link>
            </div>
            <div className="mt-12 flex gap-10">
              {[["92%", "avg match precision"], ["7", "scoring dimensions"], ["10k+", "talent profiles"]].map(
                ([a, b]) => (
                  <div key={b}>
                    <div className="font-mono2 font-bold text-3xl text-[#002FA7]">{a}</div>
                    <div className="text-xs text-[#4B5563] uppercase tracking-wide">{b}</div>
                  </div>
                )
              )}
            </div>
          </div>

          <div className="lg:col-span-5 border-l border-[#0A0A0A] bg-[#F3F4F6] flex flex-col justify-center p-8">
            <div className="bg-white border border-[#0A0A0A] p-6 hard-card">
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono2 text-xs uppercase text-[#4B5563]">Recommended for you</span>
                <span className="font-mono2 font-bold text-2xl text-[#002FA7]">98%</span>
              </div>
              <div className="font-display font-bold text-xl">Director, Supply Chain</div>
              <div className="text-sm text-[#4B5563] mb-4">Amazon Canada · Toronto</div>
              <ul className="space-y-1.5 text-sm">
                <li className="flex gap-2"><span className="text-[#002FA7]">✔</span> Your SAP experience matches</li>
                <li className="flex gap-2"><span className="text-[#002FA7]">✔</span> Healthcare supply chain background fits</li>
                <li className="flex gap-2"><span className="text-[#002FA7]">✔</span> Salary matches preference</li>
              </ul>
            </div>
            <div className="mt-4 bg-white border border-[#0A0A0A] p-5">
              <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-2">Match breakdown</div>
              {[["Skills", 35], ["Experience", 25], ["Industry", 15]].map(([l, w]) => (
                <div key={l} className="flex items-center gap-3 mb-1.5">
                  <span className="w-20 text-xs">{l}</span>
                  <div className="flex-1 h-2 bg-[#F3F4F6]"><div className="h-2 bg-[#002FA7]" style={{ width: "90%" }} /></div>
                  <span className="font-mono2 text-xs font-bold">{w}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 py-24">
        <h2 className="font-display font-black text-4xl tracking-tighter mb-12">How the engine works</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#0A0A0A] border border-[#0A0A0A]">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-white p-7">
              <div className="font-mono2 font-bold text-[#002FA7] text-sm mb-4">{s.n}</div>
              <div className="font-display font-bold text-lg mb-2">{s.t}</div>
              <p className="text-sm text-[#4B5563]">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-y border-[#0A0A0A] bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-5 py-24">
          <h2 className="font-display font-black text-4xl tracking-tighter mb-12">One platform, full pipeline</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div key={f.t} className="bg-white border border-[#E5E7EB] p-6 hard-card">
                <f.icon size={24} className="text-[#002FA7] mb-4" />
                <div className="font-display font-bold text-lg mb-1.5">{f.t}</div>
                <p className="text-sm text-[#4B5563]">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE JOBS */}
      {jobs.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 py-24">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-display font-black text-4xl tracking-tighter">Open on Darriker now</h2>
            <Link to="/auth?mode=register&role=candidate" className="text-sm font-medium text-[#002FA7] hover:underline">
              Match me →
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {jobs.map((j) => (
              <div key={j.id} className="border border-[#E5E7EB] p-6 hard-card">
                <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-2">{j.industry}</div>
                <div className="font-display font-bold text-lg">{j.title}</div>
                <div className="text-sm text-[#4B5563]">{j.company} · {j.location}</div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(j.required_skills || []).slice(0, 4).map((s) => (
                    <span key={s} className="font-mono2 text-xs border border-[#0A0A0A] px-2 py-0.5">{s}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-5 py-20 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="font-display font-black text-4xl tracking-tighter max-w-lg">
            Stop screening. Start matching.
          </h2>
          <Link
            to="/auth?mode=register"
            className="bg-[#002FA7] hover:bg-white hover:text-[#0A0A0A] px-8 py-4 font-medium inline-flex items-center gap-2 transition-colors"
          >
            Create free account <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#0A0A0A] py-10 text-center text-xs text-[#4B5563] font-mono2">
        Darriker · GlobalHire.AI — AI Talent Matching Engine
      </footer>
    </main>
  );
}
